/**
 * Training Module Constants
 * Centralized configuration for the training system
 */

// LocalStorage Keys
export const STORAGE_KEYS = {
  ACTIVE_PLAN: 'gym_notebook_active_plan',
  TRAINING_HISTORY: 'gym_notebook_training_history',
  EXERCISE_HISTORY: 'gym_notebook_exercise_history',
  EXERCISE_NOTES: 'gym_notebook_exercise_notes',
  TIMER_SETTINGS: 'fitness_timer_settings',
  ONBOARDING_COMPLETE: 'training_onboarding_complete'
} as const;

// Day type
export interface WeekDay {
  key: string;
  label: string;
  fullLabel: string;
}

// Day mappings
export const WEEKDAYS: WeekDay[] = [
  { key: 'mon', label: 'Mon', fullLabel: 'Monday' },
  { key: 'tue', label: 'Tue', fullLabel: 'Tuesday' },
  { key: 'wed', label: 'Wed', fullLabel: 'Wednesday' },
  { key: 'thu', label: 'Thu', fullLabel: 'Thursday' },
  { key: 'fri', label: 'Fri', fullLabel: 'Friday' },
  { key: 'sat', label: 'Sat', fullLabel: 'Saturday' },
  { key: 'sun', label: 'Sun', fullLabel: 'Sunday' },
];

// Flexible ("not-a-specific-weekday") day mappings. Used when the user
// picks Schedule Mode = "Flexible" in the Plan builder — trainingDays
// becomes ['day1', 'day2', ...'dayN'] instead of ['mon', 'wed', ...].
// The rest of the app resolves these via `getDayLabel` and existing
// dayKey-indexed maps (dayExercises, dayMuscleAssignments, etc.), so
// no other data structure needs to change.
export const FLEX_DAYS: WeekDay[] = [
  { key: 'day1', label: 'D1', fullLabel: 'Day 1' },
  { key: 'day2', label: 'D2', fullLabel: 'Day 2' },
  { key: 'day3', label: 'D3', fullLabel: 'Day 3' },
  { key: 'day4', label: 'D4', fullLabel: 'Day 4' },
  { key: 'day5', label: 'D5', fullLabel: 'Day 5' },
  { key: 'day6', label: 'D6', fullLabel: 'Day 6' },
  { key: 'day7', label: 'D7', fullLabel: 'Day 7' },
];

/** Returns true when a dayKey belongs to the flexible-schedule scheme
 * (day1..day7). Kept as a helper so future callers don't hard-code the
 * `dayN` prefix everywhere. */
export function isFlexDayKey(dayKey: string): boolean {
  return /^day[1-7]$/.test(dayKey);
}

// Timer settings interface
export interface TimerSettings {
  exerciseDuration: number;
  restDuration: number;
  exerciseRestDuration: number;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  volume: number;
  autoStartRest: boolean;
  supersetMode: boolean;
  supersetExerciseCount: number;
  voiceRepMode: boolean;
  voiceMode: boolean;
  // Pair-mode label — see TimerSettingsDialog for the meaning of each
  // value. Optional with default 'superset' so old localStorage configs
  // keep working.
  pairModeLabel?: 'superset' | 'comboset';
  /** FST-7 finisher rest (seconds). Small muscles recover faster, so the
   *  7-set finisher uses a shorter rest on them. Optional so existing
   *  localStorage configs keep working. */
  fst7RestSmall?: number;
  fst7RestBig?: number;
}

// Default timer settings
export const DEFAULT_TIMER_SETTINGS: TimerSettings = {
  exerciseDuration: 45,
  restDuration: 60,
  exerciseRestDuration: 90, // Rest between exercises
  soundEnabled: true,
  vibrationEnabled: true,
  volume: 70,
  autoStartRest: true,
  supersetMode: false,
  supersetExerciseCount: 4,
  voiceRepMode: false,
  voiceMode: false,
  pairModeLabel: 'superset',
  fst7RestSmall: 30,
  fst7RestBig: 45,
};

// Progressive overload rule interface
export interface OverloadRule {
  maxReps: number;
  resetReps: number;
  weightIncrease: number;
}

// Progressive overload rules
export const PROGRESSIVE_OVERLOAD: Record<string, OverloadRule> = {
  shoulders: {
    maxReps: 20,
    resetReps: 12,
    weightIncrease: 0.5,
  },
  default: {
    maxReps: 12,
    resetReps: 8,
    weightIncrease: 1,
  },
};

// Shoulder muscle identifiers
export const SHOULDER_MUSCLES: string[] = [
  'Shoulders', 'Delts', 'Deltoids', 
  'Front Delt', 'Side Delt', 'Rear Delt'
];

// Exercise set types
export const EXERCISE_TYPES: string[] = ['Regular', 'Myoreps', 'Myoreps Match', 'FST-7'];

// Timer types
export const TIMER_TYPES = {
  EXERCISE: 'exercise',
  REST: 'rest',
  EXERCISE_REST: 'exerciseRest'
} as const;

export type TimerType = typeof TIMER_TYPES[keyof typeof TIMER_TYPES];

// Timer positions
export const TIMER_POSITIONS = {
  TOP: 'top',
  MIDDLE: 'middle',
  BOTTOM: 'bottom'
} as const;

export type TimerPosition = typeof TIMER_POSITIONS[keyof typeof TIMER_POSITIONS];

// Data cleanup cutoff date
export const DATA_CLEANUP_CUTOFF = new Date('2025-01-13T00:00:00Z');

// Max history entries to keep
export const MAX_HISTORY_ENTRIES = 100;
export const MAX_EXERCISE_HISTORY_ENTRIES = 20;

// Duplicate prevention window (5 minutes in ms)
export const DUPLICATE_PREVENTION_WINDOW = 5 * 60 * 1000;
